import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(undefined);

// Default ACL Care data structure for new users
export const DEFAULT_ACL_CARE = {
  injury_side: null,
  injury_date: null,
  surgery_date: null,
  surgeon_name: null,
  acl_onboarding_completed: false,
  acl_onboarding_completed_at: null,
  current_phase: 1,
  phases: {
    phase_1: {
      unlocked: true,
      start_date: null,
      target_completions: { session_1: 8, session_2: 8, optional: 4 },
      completions: { session_1: 0, session_2: 0, optional: 0 },
      test_out_passed: false,
      test_out_passed_at: null
    },
    phase_2: {
      unlocked: false,
      unlocked_at: null,
      unlocked_method: null,
      start_date: null,
      target_completions: { session_1: 8, session_2: 8, optional: 4 },
      completions: { session_1: 0, session_2: 0, optional: 0 },
      test_out_passed: false,
      test_out_passed_at: null
    },
    phase_3: {
      unlocked: false,
      unlocked_at: null,
      unlocked_method: null,
      start_date: null,
      target_completions: { session_1: 8, session_2: 8, optional: 4 },
      completions: { session_1: 0, session_2: 0, optional: 0 },
      test_out_passed: false,
      test_out_passed_at: null
    },
    phase_4: {
      unlocked: false,
      unlocked_at: null,
      unlocked_method: null,
      start_date: null,
      target_completions: { session_1: 8, session_2: 8, optional: 4 },
      completions: { session_1: 0, session_2: 0, optional: 0 },
      test_out_passed: false,
      test_out_passed_at: null
    }
  },
  unlock_codes_used: [],
  created_at: null,
  updated_at: null
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Temporary storage for auth flow
  const [authFlowData, setAuthFlowData] = useState({});

  // Computed properties
  const isProvider = user?.is_provider || false;
  const isAdmin = user?.role === 'Admin';
  const aclCare = user?.acl_care || DEFAULT_ACL_CARE;
  const patient = user?.patient || null; // Patient record merged from /auth/me

  // Initialize user from stored token
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = api.getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // /auth/me returns user with patient merged as user.patient
        const profile = await api.getMe();
        setUser(profile);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('[Auth] Initialization error:', err);
        api.clearToken();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Step 1: Send verification code
  const sendCode = async (email) => {
    try {
      setError(null);
      const response = await api.sendVerificationCode(email);

      if (response.success) {
        setAuthFlowData({
          email,
          verificationId: response.verification_id
        });
        return { success: true };
      }

      throw new Error(response.message || 'Failed to send verification code');
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Step 2: Verify PIN code
  const verifyCode = async (email, code) => {
    try {
      setError(null);

      if (!authFlowData.verificationId) {
        throw new Error('Verification ID not found. Please restart login.');
      }

      const response = await api.verifyPinCode(email, code, authFlowData.verificationId);

      if (response.success && response.auth_token) {
        api.setToken(response.auth_token);
        setIsAuthenticated(true);

        // Fetch user profile (includes patient as user.patient)
        const profile = await api.getMe();
        setUser(profile);

        return { 
          success: true, 
          isNewUser: response.isNewUser 
        };
      }

      throw new Error(response.message || 'Invalid verification code');
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Logout
  const logout = () => {
    api.clearToken();
    setUser(null);
    setIsAuthenticated(false);
    setAuthFlowData({});
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!isAuthenticated) return;

    try {
      const profile = await api.getMe();
      setUser(profile);
    } catch (err) {
      console.error('[Auth] Failed to refresh user:', err);
    }
  };

  // Update ACL Care data
  const updateAclCare = async (updates) => {
    if (!user?.id) return { success: false };

    try {
      const newAclCare = {
        ...aclCare,
        ...updates,
        updated_at: new Date().toISOString()
      };

      await api.updateAclCare(user.id, newAclCare);
      
      setUser(prev => ({
        ...prev,
        acl_care: newAclCare
      }));

      return { success: true };
    } catch (err) {
      console.error('[Auth] Failed to update ACL Care:', err);
      return { success: false, error: err.message };
    }
  };

  // Token validation interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkToken = async () => {
      try {
        await api.getMe();
      } catch (err) {
        console.error('[Auth] Token validation failed:', err);
        logout();
      }
    };

    const interval = setInterval(checkToken, 5 * 60 * 1000); // Every 5 minutes
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value = {
    user,
    patient,  // Convenience accessor for user.patient
    loading,
    error,
    isAuthenticated,
    isProvider,
    isAdmin,
    aclCare,
    sendCode,
    verifyCode,
    logout,
    refreshUser,
    updateAclCare,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}